import { useState, useEffect, useCallback } from 'react';
import { API_URL } from '../../config';
import { useAuth } from '../../context/AuthContext';
import SiteLayout from '../../layouts/SiteLayout';
import Header from '../../components/Header/Header';
import Box from '../../components/Common/Box';

interface Message {
  id: number;
  sender_email: string;
  subject: string;
  content: string;
  created_at: string;
  is_read: boolean;
}

const AdminMessagesScreen: React.FC = () => {
  const { token } = useAuth();
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState<Message | null>(null);

  const fetchMessages = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(`${API_URL}/v1/admin/messages`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setMessages(Array.isArray(data) ? data : []);
      } else {
        setMessages([]);
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
      setMessages([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) fetchMessages();
  }, [token, fetchMessages]);

  const handleSelectMessage = async (msg: Message) => {
    setSelectedMessage(msg);
    if (!msg.is_read && token) {
      try {
        const response = await fetch(`${API_URL}/v1/admin/messages/${msg.id}/read`, {
          method: 'PUT',
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.ok) {
          // Update local state to reflect it's read
          setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, is_read: true } : m));
        }
      } catch (error) {
        console.error('Error marking message as read', error);
      }
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <SiteLayout>
      <Header icon='mail' title='Messages des utilisateurs' />
      <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
        <div style={{ display: 'grid', gridTemplateColumns: selectedMessage ? '1fr 400px' : '1fr', gap: '20px' }}>

          {/* Messages List */}
          <Box>
            <div className='box-title box-vertical-padding box-horizontal-padding'>
              <p style={{ margin: 0, fontWeight: 600, color: '#1e293b' }}>📬 Messages reçus ({messages.length})</p>
            </div>
            <div style={{ padding: '16px' }}>
              {loading ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                  Chargement des messages...
                </div>
              ) : messages.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '60px', color: '#94a3b8' }}>
                  📭 Aucun message reçu
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: 'linear-gradient(180deg, #667eea 0%, #764ba2 100%)' }}>
                      <th style={{ color: 'white', padding: '14px 16px', textAlign: 'left', fontSize: '13px', fontWeight: 600 }}>Date</th>
                      <th style={{ color: 'white', padding: '14px 16px', textAlign: 'left', fontSize: '13px', fontWeight: 600 }}>Expéditeur</th>
                      <th style={{ color: 'white', padding: '14px 16px', textAlign: 'left', fontSize: '13px', fontWeight: 600 }}>Sujet</th>
                      <th style={{ color: 'white', padding: '14px 16px', textAlign: 'center', fontSize: '13px', fontWeight: 600, width: '80px' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {messages.map((msg, index) => (
                      <tr
                        key={msg.id}
                        style={{
                          backgroundColor: selectedMessage?.id === msg.id ? 'var(--navbar-hover-bg)' : (index % 2 === 0 ? 'transparent' : 'var(--bg-hover)'),
                          cursor: 'pointer',
                          transition: 'background-color 0.2s',
                          borderLeft: selectedMessage?.id === msg.id ? '3px solid #667eea' : '3px solid transparent'
                        }}
                        onClick={() => handleSelectMessage(msg)}
                      >
                        <td style={{ padding: '12px 16px', color: '#475569', fontSize: '13px', whiteSpace: 'nowrap' }}>
                          {formatDate(msg.created_at)}
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <div style={{ fontWeight: msg.is_read ? 500 : 700, color: '#1e293b' }}>
                            {msg.sender_email || 'Anonyme'}
                            {!msg.is_read && <span style={{ marginLeft: '8px', color: '#ef4444', fontSize: '16px' }}>•</span>}
                          </div>
                        </td>
                        <td style={{ padding: '12px 16px', color: '#475569' }}>
                          <div style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: '300px',
                            fontWeight: msg.is_read ? 400 : 600
                          }}>
                            {msg.subject || '(Sans sujet)'}
                          </div>
                        </td>
                        <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSelectMessage(msg);
                            }}
                            style={{
                              backgroundColor: '#667eea',
                              color: 'white',
                              border: 'none',
                              padding: '6px 12px',
                              borderRadius: '6px',
                              fontSize: '12px',
                              cursor: 'pointer'
                            }}
                          >
                            Voir
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </Box>

          {/* Message Detail Panel */}
          {selectedMessage && (
            <Box>
              <div style={{
                padding: '16px',
                borderBottom: '1px solid #e2e8f0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <span style={{ fontWeight: 600, color: '#1e293b' }}>📧 Détails du message</span>
                <button
                  onClick={() => setSelectedMessage(null)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '20px',
                    cursor: 'pointer',
                    color: '#94a3b8'
                  }}
                >
                  ✕
                </button>
              </div>
              <div style={{ padding: '20px' }}>
                {/* Sender Info */}
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '4px' }}>Expéditeur</div>
                  <div style={{
                    fontWeight: 600,
                    color: '#1e293b',
                    padding: '10px 14px',
                    backgroundColor: '#f8fafc',
                    borderRadius: '8px'
                  }}>
                    {selectedMessage.sender_email || 'Anonyme'}
                  </div>
                </div>

                {/* Date */}
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '4px' }}>Date de réception</div>
                  <div style={{ color: '#475569' }}>
                    {formatDate(selectedMessage.created_at)}
                  </div>
                </div>

                {/* Subject */}
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '4px' }}>Sujet</div>
                  <div style={{
                    fontWeight: 600,
                    color: '#1e293b',
                    fontSize: '16px'
                  }}>
                    {selectedMessage.subject || '(Sans sujet)'}
                  </div>
                </div>

                {/* Message Content */}
                <div>
                  <div style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '8px' }}>Message</div>
                  <div style={{
                    color: '#334155',
                    lineHeight: '1.7',
                    padding: '16px',
                    backgroundColor: '#f8fafc',
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0',
                    whiteSpace: 'pre-wrap',
                    maxHeight: '300px',
                    overflowY: 'auto'
                  }}>
                    {selectedMessage.content || '(Aucun contenu)'}
                  </div>
                </div>

                {/* Actions */}
                <div style={{ marginTop: '20px' }}>
                  <button
                    style={{
                      width: '100%',
                      padding: '10px 16px',
                      backgroundColor: '#fef2f2',
                      color: '#dc2626',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: 500,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px'
                    }}
                    onClick={() => {
                      if (window.confirm('Supprimer ce message ?')) {
                        // TODO: Implement delete API call
                        setSelectedMessage(null);
                      }
                    }}
                  >
                    🗑️ Supprimer le message
                  </button>
                </div>
              </div>
            </Box>
          )}
        </div>
      </div>
    </SiteLayout>
  );
};

export default AdminMessagesScreen;

